import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { SchoolSettings as SchoolSettingsType } from '../../types';
import './SchoolSettings.css';

const SchoolSettings: React.FC = () => {
  const [settings, setSettings] = useState<SchoolSettingsType>({
    school_name: '',
    email_signature_template: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings/school');
      const data = response.data;
      setSettings({
        school_name: data.data.school_name || '',
        email_signature_template: data.data.email_signature_template || 'Regards,\n{{schoolName}}'
      });
    } catch (error) {
      console.error('Error fetching school settings:', error);
      toast.error('Failed to load school settings');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!settings.school_name?.trim()) {
      newErrors.school_name = 'School name is required';
    }

    if (!settings.email_signature_template?.trim()) {
      newErrors.email_signature_template = 'Email signature template is required';
    } else if (!settings.email_signature_template?.includes('{{schoolName}}')) {
      newErrors.email_signature_template = 'Template must include {{schoolName}} placeholder';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof SchoolSettingsType, value: string) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      await api.put('/settings/school', settings);

      toast.success('School settings saved successfully');
    } catch (error) {
      console.error('Error saving school settings:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save settings';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    fetchSettings();
    setErrors({});
  };

  const previewEmailSignature = () => {
    return (settings.email_signature_template || '').replace('{{schoolName}}', settings.school_name || '[School Name]');
  };

  if (loading) {
    return (
      <div className="school-settings-loading">
        <div className="loading-spinner"></div>
        <p>Loading school settings...</p>
      </div>
    );
  }

  return (
    <div className="school-settings">
      <div className="settings-section-header">
        <h3>School Configuration</h3>
        <p>Configure your school information and email templates</p>
      </div>

      <form onSubmit={handleSubmit} className="school-settings-form">
        <div className="form-group">
          <label htmlFor="school-name" className="form-label">
            School Name *
          </label>
          <input
            type="text"
            id="school-name"
            value={settings.school_name}
            onChange={(e) => handleInputChange('school_name', e.target.value)}
            className={`form-input ${errors.school_name ? 'error' : ''}`}
            placeholder="Enter your school name..."
            disabled={saving}
          />
          {errors.school_name && (
            <span className="error-message">{errors.school_name}</span>
          )}
          <div className="form-help">
            This name will be used in email signatures and document headers.
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="email-signature" className="form-label">
            Email Signature Template *
          </label>
          <textarea
            id="email-signature"
            value={settings.email_signature_template}
            onChange={(e) => handleInputChange('email_signature_template', e.target.value)}
            className={`form-textarea ${errors.email_signature_template ? 'error' : ''}`}
            rows={4}
            placeholder="Enter email signature template..."
            disabled={saving}
          />
          {errors.email_signature_template && (
            <span className="error-message">{errors.email_signature_template}</span>
          )}
          <div className="form-help">
            Use <code>{'{{schoolName}}'}</code> as a placeholder for the school name. 
            This template will be used in document transmittal emails.
          </div>
        </div>

        <div className="signature-preview">
          <h4>Email Signature Preview:</h4>
          <div className="preview-content">
            {previewEmailSignature().split('\n').map((line, index) => (
              <div key={index}>{line}</div>
            ))}
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleReset}
            className="btn btn-secondary"
            disabled={saving}
          >
            Reset
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="loading-spinner"></span>
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </button>
        </div>
      </form>

      <div className="settings-info">
        <div className="info-card">
          <h4>📧 Email Integration</h4>
          <p>
            These settings control how emails are sent when documents are transmitted to students. 
            Make sure your school name is accurate as it will appear in all outgoing emails.
          </p>
        </div>

        <div className="info-card">
          <h4>🔧 Template Variables</h4>
          <p>
            Available template variables:
          </p>
          <ul>
            <li><code>{'{{schoolName}}'}</code> - Your school name</li>
          </ul>
          <p>
            More template variables may be added in future updates.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SchoolSettings;